import {useNavigate} from 'react-router-dom'
import {useState} from 'react'
import {toast} from 'react-toastify'
import {adjustInventoryClient} from '../InventoryFix.client'
import {Analytics} from "../../../analytics/analytics.events";
import {useGoToInventoryProductList} from "../product-list/InventoryProductList.navigator";
import { useAtom } from 'jotai';
import { inventoryFixOriginInfoAtom, inventoryFixSelectedProductReader } from '../InventoryFix.state';

function canConfirmFix(
  quantity: string,
  currentStock: number,
  description: string
): { disabled: boolean; hasErrors?: boolean } {
  const strQuantity = quantity.toString().trim();
  const numberQuantity = Number(quantity);
  const trimmedDesc = description?.trim() ?? '';

  const isValidDescription =
    trimmedDesc.length >= 10 &&
    trimmedDesc.length <= 140 &&
    !/^\d+$/.test(trimmedDesc) &&
    /[a-zA-Z0-9]/.test(trimmedDesc) &&
    !/^([a-zA-Z0-9])\1+$/.test(trimmedDesc);

  const canConfirm =
    strQuantity !== '' &&
    strQuantity === numberQuantity.toString() &&
    isValidDescription;

  if (canConfirm) {
    return { disabled: false };
  }

  const result: { disabled: boolean; hasErrors?: boolean } = { disabled: true };

  if (!isValidDescription) {
    result.hasErrors = true;
  }

  return result;
}

export const useInventoryProductFix = () => {
  const navigate = useNavigate()

  const inventoryProductListPage = useGoToInventoryProductList();
  const [quantity, setQuantity] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  const [origin] = useAtom(inventoryFixOriginInfoAtom)
  const [selectedProduct] = useAtom(inventoryFixSelectedProductReader)
  
  return {
    canConfirm: canConfirmFix(quantity, selectedProduct.stock, description),
    value: {
      ean: selectedProduct.productEan,
      sku: selectedProduct.productSku,
      name: selectedProduct.productName,
      stock: selectedProduct.stock,
      zone: selectedProduct.productZone as string,
      isWeighable: selectedProduct.byWeight,
      image: selectedProduct.productImage as string, 
    },
    actions: {
      confirm: async () => {
        try {
          setLoading(true)
          const source = origin?.container?.name || origin?.location?.name as string 
          const numericQuantity = Number(quantity)
      
          const analyticsPayload = {
            location: origin?.location?.legacyId as string,
            zone: origin?.location?.zone as string,
            count: numericQuantity,
            ean: selectedProduct.productEan,
            warehouse: selectedProduct.warehouse,
            description,
            difference: numericQuantity - selectedProduct.stock,
            quantity: selectedProduct.stock,
          }
      
          if (numericQuantity !== selectedProduct.stock) {
            await adjustInventoryClient({
              ean: selectedProduct.productEan,
              source,
              quantity: numericQuantity,
              description
            })
      
            Analytics.inventoryFix.adjustInventory(analyticsPayload)
          } else {
            Analytics.inventoryFix.adjustInventoryFake(analyticsPayload)
          }
      
          toast.success(`Cantidad ${numericQuantity !== selectedProduct.stock ? 'actualizada' : 'confirmada'} del SKU: ${selectedProduct.productSku} a ${quantity}`)
      
          setTimeout(() => {
            setLoading(false)
            inventoryProductListPage.go(origin!)
          }, 2000)
        } catch (e) {
          console.error('On confirm inventory adjustment', e)
          toast.error(`Hubo un problema al ajustar el inventario. "${e.message ?? ''}`)
          setLoading(false)
        }
      },      
      goBack: () => {
        navigate(-1)
      },
      setQuantity,
      setDescription
    },
    origin,
    loading,
    description
  }
}
